import type { UseQueryResult } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import omit from 'lodash/omit';
import { useRouter } from 'next/router';
import React, { useCallback } from 'react';
import { animateScroll } from 'react-scroll';

import type { PaginationParams } from './types';

import type { PaginatedResources, PaginationFilters, PaginationSorting, ResourceError, ResourcePayload } from 'lib/api/resources';
import { RESOURCES, SORTING_FIELDS } from 'lib/api/resources';
import type { Params as UseApiQueryParams } from 'lib/api/useApiQuery';
import useApiQuery from 'lib/api/useApiQuery';
import getQueryParamString from 'lib/router/getQueryParamString';

export interface Params<Resource extends PaginatedResources> {
  resourceName: Resource;
  options?: UseApiQueryParams<Resource>['queryOptions'];
  pathParams?: UseApiQueryParams<Resource>['pathParams'];
  filters?: PaginationFilters<Resource>;
  sorting?: PaginationSorting<Resource>;
  scrollRef?: React.RefObject<HTMLDivElement>;
}

type NextPageParams = Record<string, unknown>;
type PageBasedPaginationData = {
  total_pages?: number;
  page?: number;
};

const INITIAL_PAGE_PARAMS = { '1': {} };
const PAGE_SIZE = 50;

function parsePositiveInteger(value: unknown) {
  const parsedValue = typeof value === 'string' || typeof value === 'number' ? Number(value) : Number.NaN;

  if (!Number.isInteger(parsedValue) || parsedValue < 1) {
    return undefined;
  }

  return parsedValue;
}

function getPageFromQuery(queryString: string | Array<string> | undefined) {
  return parsePositiveInteger(getQueryParamString(queryString)) ?? 1;
}

function getPaginationParamsFromQuery(queryString: string | Array<string> | undefined) {
  if (queryString) {
    try {
      return JSON.parse(decodeURIComponent(getQueryParamString(queryString))) as NextPageParams;
    } catch (error) {}
  }

  return {};
}

function getNextPageParams<R extends PaginatedResources>(data: ResourcePayload<R> | undefined) {
  if (!data || typeof data !== 'object') {
    return;
  }

  if (!Object.prototype.hasOwnProperty.call(data, 'next_page_params')) {
    return;
  }

  return (data as { next_page_params?: NextPageParams | null }).next_page_params;
}

function getPageBasedPaginationData<R extends PaginatedResources>(data: ResourcePayload<R> | undefined) {
  if (!data || typeof data !== 'object') {
    return;
  }

  const paginationData = data as PageBasedPaginationData;

  return {
    // The response is page-based as soon as it carries a numeric total_pages — even 0 for an empty list.
    // (parsePositiveInteger rejects 0, so we can't infer the mode from totalPages alone.)
    isPageBased: typeof paginationData.total_pages === 'number',
    totalPages: parsePositiveInteger(paginationData.total_pages),
    page: parsePositiveInteger(paginationData.page),
  };
}

export type QueryWithPagesResult<Resource extends PaginatedResources> =
UseQueryResult<ResourcePayload<Resource>, ResourceError<unknown>> &
{
  onFilterChange: <R extends PaginatedResources = Resource>(filters: PaginationFilters<R>) => void;
  onSortingChange: (sorting?: PaginationSorting<Resource>) => void;
  pagination: PaginationParams;
};

export default function useQueryWithPages<Resource extends PaginatedResources>({
  resourceName,
  filters,
  sorting,
  options,
  pathParams,
  scrollRef,
}: Params<Resource>): QueryWithPagesResult<Resource> {
  const resource = RESOURCES[resourceName];
  const queryClient = useQueryClient();
  const router = useRouter();

  const [ page, setPage ] = React.useState<number>(getPageFromQuery(router.query.page));
  const [ pageParams, setPageParams ] = React.useState<Record<number, NextPageParams>>({
    [page]: getPaginationParamsFromQuery(router.query.next_page_params),
  });
  const [ hasPages, setHasPages ] = React.useState(page > 1);
  const [ pageModeTotalPages, setPageModeTotalPages ] = React.useState(1);
  // Pagination mode is decided by the API response, not a static whitelist:
  // once an endpoint returns `total_pages` we latch into page mode (Page N of M + jump);
  // otherwise we stay in cursor mode driven by `next_page_params`.
  const [ isPageBased, setIsPageBased ] = React.useState(false);

  const isMounted = React.useRef(false);
  // Always send page/page_size. Page-based endpoints use them; cursor endpoints ignore them
  // and paginate via the stored next_page_params for the current page.
  const queryParams = { ...pageParams[page], ...filters, ...sorting, page, page_size: PAGE_SIZE };

  const scrollToTop = useCallback(() => {
    if (scrollRef?.current) {
      // Only scroll up to bring the list back into view; never jump downward past the anchor
      // (e.g. when the user is still looking at the page header above the list).
      if (scrollRef.current.getBoundingClientRect().top < 0) {
        scrollRef.current.scrollIntoView(true);
      }
      return;
    }

    animateScroll.scrollToTop({ duration: 0 });
  }, [ scrollRef ]);

  const queryResult = useApiQuery(resourceName, {
    pathParams,
    queryParams: Object.keys(queryParams).length ? queryParams : undefined,
    queryOptions: {
      staleTime: page === 1 ? 0 : Infinity,
      ...options,
    },
  });
  const { data } = queryResult;
  const nextPageParams = getNextPageParams(data);
  const pageBasedPagination = getPageBasedPaginationData(data);
  const currentPage = page;
  const totalPages = isPageBased ? (pageBasedPagination?.totalPages ?? pageModeTotalPages) : null;

  const getPageQuery = useCallback((nextPage: number) => {
    const nextQuery = omit(router.query, [ 'next_page_params' ]);

    if (nextPage <= 1) {
      return omit(nextQuery, [ 'page' ]);
    }

    return {
      ...nextQuery,
      page: String(nextPage),
    };
  }, [ router.query ]);

  const onNextPageClick = useCallback(() => {
    if (isPageBased) {
      if (!totalPages || currentPage >= totalPages) {
        return;
      }

      const nextPage = currentPage + 1;

      setHasPages(true);
      setPage(nextPage);
      scrollToTop();
      router.push({ pathname: router.pathname, query: getPageQuery(nextPage) }, undefined, { shallow: true });

      return;
    }

    if (!nextPageParams) {
      // we hide next page button if no next_page_params
      return;
    }

    setPageParams((prev) => ({
      ...prev,
      [page + 1]: nextPageParams as NextPageParams,
    }));
    setPage(prev => prev + 1);

    const nextPageQuery = {
      ...router.query,
      page: String(page + 1),
      next_page_params: encodeURIComponent(JSON.stringify(nextPageParams)),
    };

    setHasPages(true);
    scrollToTop();
    router.push({ pathname: router.pathname, query: nextPageQuery }, undefined, { shallow: true });
  }, [ currentPage, getPageQuery, isPageBased, nextPageParams, page, router, scrollToTop, totalPages ]);

  const onPrevPageClick = useCallback(() => {
    if (isPageBased) {
      if (currentPage <= 1) {
        return;
      }

      const nextPage = currentPage - 1;

      scrollToTop();
      router.push({ pathname: router.pathname, query: getPageQuery(nextPage) }, undefined, { shallow: true })
        .then(() => {
          setHasPages(nextPage > 1);
          setPage(nextPage);
        });

      return;
    }

    // returning to the first page
    // we dont have pagination params for the first page
    let nextPageQuery: typeof router.query = { ...router.query };
    if (page === 2) {
      nextPageQuery = omit(router.query, [ 'next_page_params', 'page' ]);
    } else {
      nextPageQuery.next_page_params = encodeURIComponent(JSON.stringify(pageParams[page - 1]));
      nextPageQuery.page = String(page - 1);
    }

    scrollToTop();
    router.push({ pathname: router.pathname, query: nextPageQuery }, undefined, { shallow: true })
      .then(() => {
        setPage(prev => prev - 1);
        page === 2 && queryClient.removeQueries({ queryKey: [ resourceName ] });
      });
  }, [ currentPage, getPageQuery, isPageBased, page, pageParams, queryClient, resourceName, router, scrollToTop ]);

  const resetPage = useCallback(() => {
    queryClient.removeQueries({ queryKey: [ resourceName ] });

    scrollToTop();
    const nextRouterQuery = omit(router.query, [ 'next_page_params', 'page' ]);
    router.push({ pathname: router.pathname, query: nextRouterQuery }, undefined, { shallow: true }).then(() => {
      queryClient.removeQueries({ queryKey: [ resourceName ] });
      setPage(1);
      setPageParams(INITIAL_PAGE_PARAMS);
      if (isPageBased) {
        setHasPages(false);
      }
      window.setTimeout(() => {
        // FIXME after router is updated we still have inactive queries for previously visited page (e.g third), where we came from
        // so have to remove it but with some delay :)
        queryClient.removeQueries({ queryKey: [ resourceName ], type: 'inactive' });
      }, 100);
    });
  }, [ isPageBased, queryClient, resourceName, router, scrollToTop ]);

  const onFilterChange = useCallback(<R extends PaginatedResources = Resource>(newFilters: PaginationFilters<R> | undefined) => {
    const newQuery = omit<typeof router.query>(router.query, 'next_page_params', 'page', 'filterFields' in resource ? resource.filterFields : []);
    if (newFilters) {
      Object.entries(newFilters).forEach(([ key, value ]) => {
        const isValidValue = typeof value === 'boolean' || (value && value.length);
        if (isValidValue) {
          newQuery[key] = Array.isArray(value) ? value.join(',') : (String(value) || '');
        }
      });
    }
    scrollToTop();
    router.push(
      {
        pathname: router.pathname,
        query: newQuery,
      },
      undefined,
      { shallow: true },
    ).then(() => {
      setHasPages(false);
      setPage(1);
      setPageParams(INITIAL_PAGE_PARAMS);
    });
  }, [ router, resource, scrollToTop ]);

  const onSortingChange = useCallback((newSorting: PaginationSorting<Resource> | undefined) => {
    const newQuery = {
      ...omit<typeof router.query>(router.query, 'next_page_params', 'page', SORTING_FIELDS),
      ...newSorting,
    };
    scrollToTop();
    router.push(
      {
        pathname: router.pathname,
        query: newQuery,
      },
      undefined,
      { shallow: true },
    ).then(() => {
      setHasPages(false);
      setPage(1);
      setPageParams(INITIAL_PAGE_PARAMS);
    });
  }, [ router, scrollToTop ]);

  const onPageJump = useCallback((targetPage: number) => {
    if (!isPageBased) {
      return;
    }

    const nextPage = parsePositiveInteger(targetPage);

    if (!nextPage) {
      return;
    }

    const normalizedPage = Math.min(nextPage, totalPages || 1);

    scrollToTop();
    router.push({ pathname: router.pathname, query: getPageQuery(normalizedPage) }, undefined, { shallow: true })
      .then(() => {
        setHasPages(normalizedPage > 1);
        setPage(normalizedPage);
      });
  }, [ getPageQuery, isPageBased, router, scrollToTop, totalPages ]);

  const onLastPageClick = useCallback(() => {
    if (!isPageBased || !totalPages) {
      return;
    }

    onPageJump(totalPages);
  }, [ isPageBased, onPageJump, totalPages ]);

  let hasNextPage = false;

  if (isPageBased) {
    hasNextPage = Boolean(totalPages && currentPage < totalPages);
  } else if (nextPageParams) {
    hasNextPage = Object.keys(nextPageParams).length > 0;
  }

  const pagination = {
    page: currentPage,
    onNextPageClick,
    onPrevPageClick,
    resetPage,
    hasPages,
    hasNextPage,
    canGoBackwards: isPageBased ? currentPage > 1 : Boolean(pageParams[page - 1]),
    isLoading: queryResult.isPlaceholderData,
    isVisible: isPageBased ? true : hasPages || hasNextPage,
    totalPages,
    canGoToLast: isPageBased ? Boolean(totalPages && currentPage < totalPages) : false,
    onLastPageClick: isPageBased ? onLastPageClick : undefined,
    onPageJump: isPageBased ? onPageJump : undefined,
  };

  React.useEffect(() => {
    if (page !== 1 && isMounted.current) {
      queryClient.cancelQueries({ queryKey: [ resourceName ] });
      setPage(1);
    }
    // re-detect the pagination mode for the new resource and drop the previous resource's page count
    // (otherwise an empty page-based resource — total_pages: 0 — would inherit the old total, e.g. "Page 1 of 9238")
    setIsPageBased(false);
    setPageModeTotalPages(1);
  // hook should run only when queryName has changed
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ resourceName ]);

  // Detect page-based endpoints from a real (non-placeholder) response and latch the mode in.
  React.useEffect(() => {
    if (queryResult.isPlaceholderData || isPageBased) {
      return;
    }

    if (pageBasedPagination?.isPageBased) {
      setIsPageBased(true);
    }
  }, [ queryResult.isPlaceholderData, isPageBased, pageBasedPagination?.isPageBased ]);

  React.useEffect(() => {
    window.setTimeout(() => {
      isMounted.current = true;
    }, 0);
  }, []);

  React.useEffect(() => {
    // Don't trust the echoed page when the endpoint reports no real pages (empty list) — otherwise we'd
    // fight the clamp below and keep restoring a stale out-of-range page.
    if (!isPageBased || !pageBasedPagination?.page || !pageBasedPagination?.totalPages || pageBasedPagination.page === page) {
      return;
    }

    setPage(pageBasedPagination.page);
    setHasPages(pageBasedPagination.page > 1);
  }, [ isPageBased, page, pageBasedPagination?.page, pageBasedPagination?.totalPages ]);

  React.useEffect(() => {
    if (!isPageBased || !pageBasedPagination?.totalPages) {
      return;
    }

    setPageModeTotalPages(pageBasedPagination.totalPages);
  }, [ isPageBased, pageBasedPagination?.totalPages ]);

  // Snap back into range when the current page exceeds the available pages — e.g. a stale page carried
  // over from another tab, or an empty result (total_pages: 0 → effective 1). Avoids "Page 3 of 1".
  React.useEffect(() => {
    if (!isPageBased || queryResult.isPlaceholderData || !totalPages || page <= totalPages) {
      return;
    }

    setPage(totalPages);
    setHasPages(totalPages > 1);
    router.push({ pathname: router.pathname, query: getPageQuery(totalPages) }, undefined, { shallow: true });
  }, [ isPageBased, page, totalPages, queryResult.isPlaceholderData, getPageQuery, router ]);

  return { ...queryResult, pagination, onFilterChange, onSortingChange };
}
